import { useState } from "react";
import { FaUserCheck, FaUserSlash } from "react-icons/fa6";

// components
import DialogModal from "../dialog-modal/dialog-modal.component";
import { Button } from "../ui/button";
import FormError from "../form-error/form-error.component";

// global functions and hooks
import { queryClient } from "../../global/query-client";
import {
	EmployeePermissions,
	employeesQueryKey,
	useEmployeeDetails,
	useUpdateUserStatus,
} from "../../queries/employee.queries";
import { useSession } from "../../queries/auth.queries";

interface EmployeeStatusToggleProps {
	userInfoId: string;
	empPermission?: EmployeePermissions;
}

// Split out alongside EmployeeLoginManager: company_user_status is a
// company-scoped relationship flag, distinct from both the personal-fields
// form and login credentials — its own affordance, own confirm dialog, own
// mutation (PUT /user/status), independent of everything else on the profile.
// Self-contained: given just a userInfoId it fetches its own
// useEmployeeDetails (deduped against the profile's own call to the same key)
// for both the current status and the role check below.
function EmployeeStatusToggle({
	userInfoId,
	empPermission,
}: EmployeeStatusToggleProps) {
	const { data: session } = useSession();
	const { data: employeeDetails } = useEmployeeDetails(userInfoId);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const statusMutation = useUpdateUserStatus();

	const isOwner = employeeDetails?.user_role === "owner";
	const isSelf = userInfoId === session?.user_info_id;
	// Same "not an owner" guard the Delete button already applies — and self
	// is excluded too: the backend already blocks self-deactivation
	// (self-lockout), and reactivating yourself isn't reachable anyway since
	// an inactive session couldn't have loaded this screen.
	if (!empPermission?.edit || isOwner || isSelf) return null;

	const isActive = employeeDetails?.company_user_status !== "inactive";

	const handleConfirm = async () => {
		try {
			await statusMutation.mutateAsync({
				user_info_id: userInfoId,
				company_user_status: isActive ? "inactive" : "active",
			});
		} catch {
			return; // FormError below renders the message
		}

		queryClient.invalidateQueries({ queryKey: employeesQueryKey });
		queryClient.invalidateQueries({
			queryKey: ["employeeDetails", userInfoId],
		});
		statusMutation.reset();
		setConfirmOpen(false);
	};

	return (
		<>
			<Button
				variant={isActive ? "destructive" : "outline"}
				size="sm"
				type="button"
				onClick={() => setConfirmOpen(true)}
			>
				{isActive ? (
					<>
						<FaUserSlash /> Deactivate
					</>
				) : (
					<>
						<FaUserCheck /> Reactivate
					</>
				)}
			</Button>
			<DialogModal
				open={confirmOpen}
				onOpenChange={(open) => {
					if (!open) statusMutation.reset();
					setConfirmOpen(open);
				}}
				title={
					isActive ? "Deactivate employee?" : "Reactivate employee?"
				}
				description={
					isActive
						? "This revokes their access to this company and signs them out everywhere. Their history is preserved, and this can be undone from this same profile at any time."
						: "This restores their access to this company."
				}
				footer={
					<>
						<Button
							variant="outline"
							onClick={() => setConfirmOpen(false)}
							disabled={statusMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							onClick={handleConfirm}
							disabled={statusMutation.isPending}
						>
							{statusMutation.isPending
								? "Saving..."
								: isActive
									? "Deactivate"
									: "Reactivate"}
						</Button>
					</>
				}
			>
				<FormError
					isError={statusMutation.isError}
					error={statusMutation.error}
					fallback="Something went wrong. Please try again."
				/>
			</DialogModal>
		</>
	);
}

export default EmployeeStatusToggle;
